import { useState, useMemo } from "react";
import { EventHeader } from "./EventHeader";
import { UnifiedFilters } from "./UnifiedFilters";
import { EventGrid } from "./EventGrid";
import { eventsData } from "./eventsData";

export type Day = "all" | "day1" | "day2";
export type Category = "all" | "flagship" | "technical" | "non-technical" | "games";

const styles = `
  @keyframes float {
    0%, 100% { transform: translateY(0) rotate(0deg); }
    50% { transform: translateY(-20px) rotate(2deg); }
  }
  @keyframes pulse-glow {
    0%, 100% { opacity: 0.4; transform: scale(1); }
    50% { opacity: 0.7; transform: scale(1.05); }
  }
  @keyframes fade-in {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const EventPage = () => {
  // Default to "all" to show all events
  const [selectedDay, setSelectedDay] = useState<Day>("all");
  const [selectedCategory, setSelectedCategory] = useState<Category>("all");
  const [searchQuery, setSearchQuery] = useState("");

  // Filter events based on all criteria
  const currentEvents = useMemo(() => {
    let events = [];
    
    // If "all" is selected for day, combine events from both days
    if (selectedDay === "all") {
      const day1Events = Object.values(eventsData.day1 || {}).flat();
      const day2Events = Object.values(eventsData.day2 || {}).flat();
      events = [...day1Events, ...day2Events];
    } else {
      // If specific day is selected, get events for that day
      const dayEvents = eventsData[selectedDay];
      
      // If "all" is selected for category, combine all categories
      if (selectedCategory === "all") {
        events = Object.values(dayEvents).flat();
      } else {
        events = dayEvents[selectedCategory] || [];
      }
    }
    
    // Apply search filter if there's a search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      return events.filter(event => 
        event.name.toLowerCase().includes(query) ||
        (event.description && event.description.toLowerCase().includes(query))
      );
    }
    
    return events;
  }, [selectedDay, selectedCategory, searchQuery]);

  // Show all events (reset all filters)
  const showAllEvents = () => {
    setSelectedDay("all");
    setSelectedCategory("all");
    setSearchQuery("");
  };

  // Clear specific filters
  const clearDayFilter = () => {
    setSelectedDay("all");
  };

  const clearCategoryFilter = () => {
    setSelectedCategory("all");
  };

  const clearSearchFilter = () => {
    setSearchQuery("");
  };

  return (
    <>
      <style>{styles}</style>
      <div 
        style={{ 
          minHeight: '100vh',
          overflow: 'hidden',
          position: 'relative',
          backgroundColor: 'var(--color-background)',
          background: 'linear-gradient(145deg, var(--color-background) 0%, var(--color-background-alt) 50%, var(--color-surface) 100%)',
        }}
      >
        {/* Animated background elements */}
        <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
          {/* Primary color orb */}
          <div 
            style={{
              position: 'absolute',
              top: '5%',
              left: '15%',
              width: 'min(350px, 50vw)',
              height: 'min(350px, 50vw)',
              background: 'radial-gradient(circle, var(--color-primary) 0%, transparent 70%)',
              opacity: '0.12',
              borderRadius: '50%',
              filter: 'blur(60px)',
              animation: 'float 8s ease-in-out infinite',
            }}
          />
          {/* Secondary color orb */}
          <div 
            style={{
              position: 'absolute',
              bottom: '15%',
              right: '10%',
              width: 'min(300px, 45vw)',
              height: 'min(300px, 45vw)',
              background: 'radial-gradient(circle, var(--color-secondary) 0%, transparent 70%)',
              opacity: '0.1',
              borderRadius: '50%',
              filter: 'blur(80px)',
              animation: 'float 10s ease-in-out infinite 2s',
            }}
          />
          {/* Accent color orb */}
          <div 
            style={{
              position: 'absolute',
              top: '40%',
              right: '5%',
              width: 'min(180px, 30vw)',
              height: 'min(180px, 30vw)',
              background: 'radial-gradient(circle, var(--color-accent) 0%, transparent 70%)',
              opacity: '0.1',
              borderRadius: '50%',
              filter: 'blur(40px)',
              animation: 'pulse-glow 6s ease-in-out infinite',
            }}
          />
          {/* Subtle pattern overlay */}
          <div 
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `radial-gradient(var(--color-text-muted) 1px, transparent 1px)`,
              backgroundSize: '30px 30px',
              opacity: '0.03',
            }}
          />
        </div>

        {/* Main content */}
        <div 
          style={{
            position: 'relative',
            zIndex: 10,
            maxWidth: '1400px',
            margin: '0 auto',
            padding: 'clamp(16px, 4vw, 48px) clamp(12px, 3vw, 24px)',
          }}
        >
          {/* Header Section */}
          <div style={{ marginBottom: 'clamp(24px, 5vw, 40px)', animation: 'fade-in 0.8s ease-out forwards' }}>
            <EventHeader />
          </div>

          {/* Unified Filters Section */}
          <UnifiedFilters
            selectedDay={selectedDay}
            selectedCategory={selectedCategory}
            onDayChange={setSelectedDay}
            onCategoryChange={setSelectedCategory}
            onSearchChange={setSearchQuery}
            searchQuery={searchQuery}
            showAllEvents={showAllEvents}
            clearDayFilter={clearDayFilter}
            clearCategoryFilter={clearCategoryFilter}
            clearSearchFilter={clearSearchFilter}
          />

          {/* Events Grid Section */}
          <div style={{ marginBottom: 'clamp(32px, 6vw, 48px)' }}>
            <div 
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: '12px',
                marginBottom: 'clamp(16px, 3vw, 24px)',
                animation: 'fade-in 0.8s ease-out 0.4s backwards',
              }}
            >
              <h2 
                style={{
                  fontSize: 'clamp(1.5rem, 5vw, 2.5rem)',
                  fontWeight: 900,
                  background: 'linear-gradient(135deg, var(--color-secondary) 0%, var(--color-primary) 50%, var(--color-accent) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Events
              </h2>
              <span 
                style={{
                  marginLeft: 'auto',
                  color: 'var(--color-text)',
                  fontWeight: 600,
                  fontSize: 'clamp(0.8rem, 2vw, 1rem)',
                  padding: '6px 16px',
                  backgroundColor: 'var(--color-background-alt)',
                  borderRadius: '20px',
                  border: '1px solid var(--color-border)',
                }}
              >
                {currentEvents.length} {currentEvents.length === 1 ? 'event' : 'events'}
              </span>
            </div>
            <EventGrid events={currentEvents} />
          </div>
        </div>
      </div>
    </>
  );
};

export default EventPage;